import { useEffect, useId, useRef, type RefObject } from "react";
import { createPortal } from "react-dom";
import cogMascot from "../assets/cog-mascot.png";
import type { GovernedLibraryDocumentSummary } from "../api/cognitoryLibraryClient";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_PANEL_HEADING_STYLE,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
} from "../theme/mainPageVisual";
import "./CognitoryLibraryShell.css";

/** Governed delete dialog phase strings (lock contract: Deleting… / Rebuilding library… / Removed). */
export const GOVERNED_DELETE_DIALOG_PHASE_LABELS = {
  deleting: "Deleting…",
  deletingDocument: "Deleting document…",
  rebuilding: "Rebuilding library…",
  removed: "Removed",
} as const;

export type CognitoryLibraryDeleteDialogProps = {
  target: GovernedLibraryDocumentSummary;
  confirmText: string;
  deleting: boolean;
  deletePhase:
    | "idle"
    | "deleting"
    | "rebuilding_library"
    | "removed"
    | "failed";
  error: string | null;
  onConfirmTextChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  returnFocusRef: RefObject<HTMLButtonElement | null>;
};

function resolveDeleteProgressCopy(
  deletePhase: CognitoryLibraryDeleteDialogProps["deletePhase"],
): { primary: string; secondary: string | null } {
  if (deletePhase === "rebuilding_library") {
    return {
      primary: GOVERNED_DELETE_DIALOG_PHASE_LABELS.rebuilding,
      secondary: "Updating the active corpus after removal.",
    };
  }
  if (deletePhase === "removed") {
    return {
      primary: GOVERNED_DELETE_DIALOG_PHASE_LABELS.removed,
      secondary: null,
    };
  }
  return {
    primary: GOVERNED_DELETE_DIALOG_PHASE_LABELS.deletingDocument,
    secondary: "Removing the source and governed artifacts.",
  };
}

export function CognitoryLibraryDeleteDialog({
  target,
  confirmText,
  deleting,
  deletePhase,
  error,
  onConfirmTextChange,
  onCancel,
  onConfirm,
  returnFocusRef,
}: CognitoryLibraryDeleteDialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const confirmLabelId = useId();
  const confirmInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmValid = confirmText.trim() === target.documentId;
  const progressCopy = resolveDeleteProgressCopy(deletePhase);

  useEffect(() => {
    const focusReturnTarget = returnFocusRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      if (!deleting) {
        confirmInputRef.current?.focus();
      }
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !deleting) {
        event.preventDefault();
        onCancel();
      }
      if (event.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) {
          return;
        }
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      focusReturnTarget?.focus();
    };
  }, [deleting, onCancel, returnFocusRef]);

  return createPortal(
    <div
      className="cognitory-library-delete-modal-root"
      data-testid="cognitory-library-delete-modal-root"
      data-modal-layout="viewport-fixed"
      data-cognitory-visual-shell="main-page"
    >
      <div
        className="cognitory-library-delete-modal-backdrop"
        data-testid="cognitory-library-delete-modal-backdrop"
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        data-testid="cognitory-library-delete-dialog"
        className="cognitory-library-delete-modal-panel"
        style={{ fontFamily: MAIN_PAGE_FONT_STACK }}
      >
        <div className="cognitory-library-delete-modal-brand">
          <img
            src={cogMascot}
            alt=""
            aria-hidden="true"
            data-testid="cognitory-library-delete-cog"
            className="cognitory-library-delete-modal-cog"
          />
        </div>
        <p
          id={titleId}
          className="cognitory-library-delete-modal-title cognitory-panel-heading"
          data-testid="cognitory-library-delete-dialog-title"
          style={{
            ...MAIN_PAGE_PANEL_HEADING_STYLE,
            marginBottom: "0.35rem",
            fontWeight: 500,
            color: MAIN_PAGE_COLORS.inkStrong,
          }}
        >
          Delete governed document
        </p>

        {deleting ? (
          <div
            className="cognitory-library-delete-modal-progress"
            data-testid="cognitory-library-delete-progress"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <img
              src={cogMascot}
              alt=""
              aria-hidden="true"
              className="cognitory-library-delete-modal-progress-cog"
              data-testid="cognitory-library-delete-spinner"
            />
            <div>
              <p
                className="cognitory-library-delete-modal-progress-primary"
                style={{ color: MAIN_PAGE_COLORS.inkStrong }}
              >
                {progressCopy.primary}
              </p>
              {progressCopy.secondary ? (
                <p
                  className="cognitory-library-delete-modal-progress-secondary"
                  style={{ color: MAIN_PAGE_COLORS.slate }}
                >
                  {progressCopy.secondary}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <p
              id={descriptionId}
              className="cognitory-library-delete-modal-body"
              style={{ color: MAIN_PAGE_COLORS.slate }}
            >
              This permanently removes the source PDF and its derived Cognitory
              artifacts. A deletion audit record will remain.
            </p>
            <p
              className="cognitory-library-delete-modal-filename"
              data-testid="cognitory-library-delete-document-name"
              style={{ color: MAIN_PAGE_COLORS.inkStrong }}
            >
              {target.title}
            </p>
            <label
              id={confirmLabelId}
              htmlFor="cognitory-library-delete-confirm-field"
              className="cognitory-library-delete-modal-label"
              style={{ color: MAIN_PAGE_COLORS.slate }}
            >
              Type <code>{target.documentId}</code> to confirm
            </label>
            <input
              ref={confirmInputRef}
              id="cognitory-library-delete-confirm-field"
              type="text"
              data-testid="cognitory-library-delete-confirm-input"
              value={confirmText}
              onChange={event => onConfirmTextChange(event.target.value)}
              className="cognitory-library-delete-modal-input"
              autoComplete="off"
              spellCheck={false}
              aria-labelledby={confirmLabelId}
            />
          </>
        )}

        {error ? (
          <p
            data-testid="cognitory-library-delete-error"
            className="cognitory-library-delete-modal-error"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {!deleting ? (
          <div className="cognitory-library-delete-modal-actions">
            <button
              type="button"
              data-testid="cognitory-library-delete-cancel"
              className="cognitory-library-delete-modal-cancel"
              style={{
                ...MAIN_PAGE_QUIET_BUTTON_STYLE,
                marginTop: 0,
                fontFamily: MAIN_PAGE_FONT_STACK,
              }}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="cognitory-library-delete-confirm"
              disabled={!confirmValid}
              className="cognitory-library-delete-modal-confirm"
              style={{ fontFamily: MAIN_PAGE_FONT_STACK }}
              onClick={() => void onConfirm()}
            >
              Delete permanently
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
